import { SwipeableDrawer as SwipeableDrawerBase } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { DrawerContext } from "@/components/Drawer/DrawerContext";
import { useIsVirtualKeyboardOpen } from "@/hooks/useIsVirtualKeyboardOpen";

type Props = {
  open: boolean;
  onClose: () => void;
  onOpened?: () => void;
  disableRestoreFocus?: boolean;
  hasFullHeight?: boolean;
  heightOffset?: number;
  backgroundColor?: "white" | "background";
  children: React.ReactNode;
};

export function SwipeableDrawer({
  children,
  hasFullHeight,
  heightOffset = 40,
  backgroundColor = "white",
  open,
  onOpened,
  ...rest
}: Props) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hasTopBar, setHasTopBar] = useState(false);

  const { isVirtualKeyboardOpen, virtualKeyboardHeight } =
    useIsVirtualKeyboardOpen();

  const onTopBarMount = useCallback(() => setHasTopBar(true), []);
  const onTopBarUnmount = useCallback(() => setHasTopBar(false), []);

  const contentWrapperRef = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;

    setIsScrolled(element.scrollTop > 0);
    const handleScroll = () => setIsScrolled(element.scrollTop > 0);
    element.addEventListener("scroll", handleScroll);

    return () => element.removeEventListener("scroll", handleScroll);
  }, []);

  const heightOffsetWithUnit = `${heightOffset}px`;

  const heightStyle = hasFullHeight
    ? `calc(100dvh - ${heightOffsetWithUnit})`
    : "auto";

  const contextValue = useMemo(
    () => ({
      isSwipeable: true,
      isScrolled,
      backgroundColor,
      onTopBarMount,
      onTopBarUnmount,
    }),
    [isScrolled, backgroundColor, onTopBarMount, onTopBarUnmount],
  );

  return (
    <SwipeableDrawerBase
      anchor="bottom"
      open={open}
      onOpen={() => {}} // required prop, but we don't need to do anything on open
      sx={{
        "& .MuiDrawer-paper": {
          height: heightStyle,
          backgroundColor:
            backgroundColor === "white"
              ? "var(--color-white)"
              : "var(--color-surface-background)",
          paddingTop: 0,
          px: 0,
          ...(isVirtualKeyboardOpen
            ? { paddingBottom: `${virtualKeyboardHeight}px` }
            : {}),
        },
      }}
      ModalProps={{
        keepMounted: false,
      }}
      slotProps={{
        transition: {
          // Using this to notify the owner component that the drawer is opening, so it can perform necessary actions
          // The reason why `onOpen` prop of `SwipeableDrawer` is not used is because despite its name,
          // it's actually called when internal Drawer components are requesting to open.
          onEnter: onOpened,
        },
      }}
      {...rest}
    >
      <DrawerContext.Provider value={contextValue}>
        <div
          ref={contentWrapperRef}
          className="flex grow-1 flex-col overflow-y-auto overscroll-x-none"
        >
          {!hasTopBar && (
            <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-gray-300" />
          )}
          {children}
        </div>
      </DrawerContext.Provider>
    </SwipeableDrawerBase>
  );
}
